/**
 * Mountable Scores experience.
 *
 * Used by:
 *   • ScoresApp.js (standalone variant) → mounts at `/` (basePath="").
 *   • App.js main SmartKick site → mounts at `/live-scores/*` (basePath="/live-scores").
 *
 * The single source of truth for layout, nav, modal/funnel CTAs and route
 * structure. All Link/navigate paths inside the scores tree go through
 * `useScoresPath` / `useScoresNavigate` so they resolve to the right
 * prefix in either mount mode.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ScoresLayout from './ScoresLayout';
import ScoresMatches from './ScoresMatches';
import ScoresFavorites from './ScoresFavorites';
import ScoresNews from './ScoresNews';
import ScoresOffers from './ScoresOffers';
import ScoresMatchDetail from './ScoresMatchDetail';
import ScoresTeamProfile from './ScoresTeamProfile';
import PredictionsPromoModal from './PredictionsPromoModal';
import ScoresSearchOverlay from './ScoresSearchOverlay';
import { ScoresBasePathProvider } from './scoresPath';
import { usePushNotifications, syncScoresPushPreferences } from './usePushNotifications';
import { trackEvent } from '../utils/analytics';

const isNativePlatform = () =>
  typeof window !== 'undefined'
  && window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

/**
 * `basePath` — e.g. "" (standalone) or "/live-scores" (nested in main app).
 * `predictionsHref` — used as the marketing landing target on web for
 *   AI-Predictions CTAs. On native iOS we open the promo modal instead.
 */
const ScoresRoutes = ({ basePath = '', predictionsHref = '/welcome' }) => {
  const [promoOpen, setPromoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const push = usePushNotifications();
  const { user } = useAuth();
  // A user counts as a "subscriber" if they have a paid plan or are in trial.
  // For these users the funnel banner becomes a shortcut into Predictions
  // instead of a free-trial pitch.
  const isSubscriber = !!user && ['basic', 'pro', 'vip', 'trial'].includes(user.subscription_status);

  // On web, AI-Predictions CTAs route to the marketing landing page that
  // describes the full SmartKick offering + free-trial / Pro / VIP signup.
  // On native iOS we keep the in-app modal to stay Apple-friendly.
  const native = isNativePlatform();
  // Only the standalone Live Scores app (basePath="") is an acquisition
  // channel; the nested /live-scores tab inside the main app is not.
  const isStandaloneScores = basePath === '';
  const onPredictionsCtaClick = () => {
    if (isStandaloneScores) {
      // Funnel tracking: record the CTA tap + flag the device so a trial
      // started afterwards is attributed to the Scores app.
      trackEvent('predictions_cta');
      try { localStorage.setItem('sk_scores_referral', String(Date.now())); } catch { /* ignore */ }
    }
    if (native) {
      setPromoOpen(true);
    } else {
      // Use a hard navigation — landing page lives outside the scores
      // route subtree, so router-level navigation would still work,
      // but a full reload guarantees the marketing site is rendered
      // with its own providers, analytics, etc.
      window.location.href = predictionsHref;
    }
  };

  // Push permission bootstrap — only on native, once.
  useEffect(() => {
    if (!push.supported) return;
    try {
      const alreadyPrompted = localStorage.getItem('sk_scores_push_prompted') === '1';
      if (alreadyPrompted) return;
      localStorage.setItem('sk_scores_push_prompted', '1');
    } catch { /* ignore */ }
    push.request();
  }, [push]);

  // Sync favorites → backend (debounced) on any favourite-toggle event.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const teams = JSON.parse(localStorage.getItem('sk_scores_fav_teams_v1') || '[]');
          const matches = JSON.parse(localStorage.getItem('sk_scores_fav_matches_v1') || '[]');
          syncScoresPushPreferences({
            favorite_team_ids: Array.isArray(teams) ? teams : [],
            favorite_match_ids: Array.isArray(matches) ? matches : [],
          });
        } catch { /* ignore */ }
      }, 600);
    };
    window.addEventListener('sk-scores-favorites-changed', handler);
    return () => {
      window.removeEventListener('sk-scores-favorites-changed', handler);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Standalone promo nudge — only relevant on native AND only for users
  // who aren't already paying (no point pitching a trial to a VIP). On
  // web the marketing landing page is one tap away and we don't want a
  // popup blocking it.
  useEffect(() => {
    if (!native) return;
    if (isSubscriber) return;
    if (typeof window === 'undefined') return;
    const lastSeen = Number(localStorage.getItem('sk_scores_promo_seen_at') || 0);
    const oneDay = 24 * 60 * 60 * 1000;
    if (Date.now() - lastSeen < oneDay) return;
    const t = setTimeout(() => {
      setPromoOpen(true);
      localStorage.setItem('sk_scores_promo_seen_at', String(Date.now()));
    }, 30_000);
    return () => clearTimeout(t);
  }, [native, isSubscriber]);

  return (
    <ScoresBasePathProvider value={basePath}>
      <div className="scores-app-root min-h-screen bg-[#070b14] text-white font-['Manrope',sans-serif]" data-testid="scores-app-root">
        <ScoresLayout
          onPredictionsCtaClick={onPredictionsCtaClick}
          onSearchClick={() => setSearchOpen(true)}
          isSubscriber={isSubscriber}
        >
          <Routes>
            <Route path="/" element={<ScoresMatches onPredictionsCtaClick={onPredictionsCtaClick} ensurePush={push.request} />} />
            <Route path="news" element={<ScoresNews />} />
            <Route path="offers" element={native ? <Navigate to="" replace /> : <ScoresOffers />} />
            <Route path="favorites" element={<ScoresFavorites />} />
            <Route path="match/:id" element={<ScoresMatchDetail onPredictionsCtaClick={onPredictionsCtaClick} />} />
            <Route path="team/:id" element={<ScoresTeamProfile />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Routes>
        </ScoresLayout>
        <ScoresSearchOverlay
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
        />
        <PredictionsPromoModal open={promoOpen} onClose={() => setPromoOpen(false)} />
      </div>
    </ScoresBasePathProvider>
  );
};

export default ScoresRoutes;
